import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Switch,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";
import { colors } from "./source.styles";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

const EditSourceSettingsModal = ({ open, onClose, sourceData }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    sourceName: sourceData?.name || "",
    urlParameter: sourceData?.urlParameter || "",
    monthlyBudget: sourceData?.monthlyBudget || "",
    alertThreshold: sourceData?.alertThreshold || "85",
    targetLeads: sourceData?.targetLeads || "",
    expectedConversion: sourceData?.expectedConversion || "",
    activeStatus: sourceData?.activeStatus ?? true,
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleToggleStatus = (event) => {
    setFormData({ ...formData, activeStatus: event.target.checked });
  };

  const handleSubmit = () => {
    console.log("Updating source settings:", formData);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          borderBottom: `1px solid ${colors.borderLight}`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: colors.textPrimary,
                mb: 0.5,
              }}
            >
              Edit Source Settings
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: colors.textSecondary,
              }}
            >
              {formData.sourceName}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: colors.textMuted,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* SOURCE CONFIGURATION */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <TuneIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                }}
              >
                Source Configuration
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Source Name"
                fullWidth
                size="small"
                value={formData.sourceName}
                onChange={handleChange("sourceName")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    "& fieldset": {
                      borderColor: colors.borderLight,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 13,
                  },
                }}
              />
              <TextField
                label="URL / UTM Parameter"
                fullWidth
                size="small"
                value={formData.urlParameter}
                onChange={handleChange("urlParameter")}
                placeholder="utm_source=facebook&utm_medium=cpc"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    "& fieldset": {
                      borderColor: colors.borderLight,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 13,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* BUDGET MANAGEMENT */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <AccountBalanceWalletIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                }}
              >
                Budget Management
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Monthly Budget (₹)"
                fullWidth
                size="small"
                type="number"
                value={formData.monthlyBudget}
                onChange={handleChange("monthlyBudget")}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    "& fieldset": {
                      borderColor: colors.borderLight,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 13,
                  },
                }}
              />
              <TextField
                label="Alert Threshold (%)"
                fullWidth
                size="small"
                type="number"
                value={formData.alertThreshold}
                onChange={handleChange("alertThreshold")}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    "& fieldset": {
                      borderColor: colors.borderLight,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 13,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* TARGETING GOALS */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <TrackChangesIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: colors.textMuted,
                  textTransform: "uppercase",
                }}
              >
                Targeting Goals
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Target Leads / Month"
                fullWidth
                size="small"
                type="number"
                value={formData.targetLeads}
                onChange={handleChange("targetLeads")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    "& fieldset": {
                      borderColor: colors.borderLight,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 13,
                  },
                }}
              />
              <TextField
                label="Expected Conversion %"
                fullWidth
                size="small"
                type="number"
                value={formData.expectedConversion}
                onChange={handleChange("expectedConversion")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                    "& fieldset": {
                      borderColor: colors.borderLight,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 13,
                  },
                }}
              />
            </Stack>
          </Box>

          {/* Active Status */}
          <Box
            sx={{
              borderTop: `1px solid ${colors.borderLight}`,
              pt: 2,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  Active Status
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.textSecondary,
                  }}
                >
                  Disable to pause tracking and reporting for this source
                </Typography>
              </Box>
              <Switch
                checked={formData.activeStatus}
                onChange={handleToggleStatus}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: `1px solid ${colors.borderLight}`,
        }}
      >
        <Button
          onClick={handleCancel}
          variant="text"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            color: colors.textMuted,
            px: 3,
            py: 1,
            "&:hover": {
              bgcolor: colors.bgPrimary,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            bgcolor: theme.palette.primary.main,
            color: "#FFF",
            px: 3,
            py: 1,
            boxShadow: "none",
            "&:hover": {
              bgcolor: colors.primary,
              boxShadow: "none",
            },
          }}
        >
          Update Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSourceSettingsModal;
